import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, Shield, Zap, Award, Star, Plane, Gift, CreditCard, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function DeferaVsCompetitors() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonicalUrl = 'https://defera.app/guides/defera-vs-affirm-klarna-uplift';
  const metaTitle = 'Defera vs Affirm vs Klarna vs Uplift: BNPL Flight Comparison (2026)';
  const metaDescription = 'Compare Defera to Affirm, Klarna, Uplift, and other BNPL providers for flights. See why Defera is the only service with semi-private flights, gift card acceptance, and no credit checks.';

  const comparisonFeatures = [
    {
      feature: 'Semi-private flights (JSX)',
      defera: true,
      affirm: false,
      klarna: false,
      uplift: false
    },
    {
      feature: 'Accepts airline gift cards',
      defera: true,
      affirm: false,
      klarna: false,
      uplift: 'Varies by airline'
    },
    {
      feature: 'Accepts travel credits',
      defera: true,
      affirm: false,
      klarna: false,
      uplift: 'Varies by airline'
    },
    {
      feature: 'No credit check',
      defera: true,
      affirm: 'Soft check',
      klarna: 'Soft check',
      uplift: 'Soft check'
    },
    {
      feature: 'No interest',
      defera: true,
      affirm: 'Sometimes',
      klarna: 'Sometimes',
      uplift: 'Sometimes'
    },
    {
      feature: 'Weekly or bi-weekly payments',
      defera: true,
      affirm: 'Pay in 4 only',
      klarna: 'Pay in 4 only',
      uplift: false
    },
    {
      feature: 'Travel-first platform',
      defera: true,
      affirm: false,
      klarna: false,
      uplift: true
    }
  ];

  const providerDetails = [
    {
      name: 'Defera',
      tagline: 'Built for travel, not retail',
      pros: [
        'World\'s first BNPL with semi-private flights',
        'Accepts airline gift cards and travel credits',
        'No credit check, no interest',
        'Flexible weekly or biweekly installments',
        'Trip price secured once your booking is approved'
      ],
      cons: [
        'Trips must depart at least 22 days out',
        'Airline confirmation number arrives once the plan is paid in full',
        'Newer platform (founded 2024)'
      ],
      bestFor: 'Travelers who want maximum flexibility, premium options, and to use existing credits'
    },
    {
      name: 'Affirm',
      tagline: 'Pay-over-time lender',
      pros: [
        'Wide merchant acceptance, including some airlines (e.g., American)',
        'Interest-free Pay in 4 option (every 2 weeks)',
        'No late fees'
      ],
      cons: [
        'No semi-private flights',
        'Doesn\'t redeem airline gift cards or credits itself',
        'Soft credit check to see your plans',
        'Up to 36% APR on monthly plans'
      ],
      bestFor: 'Shoppers who want their booking confirmed at checkout and qualify for a 0% or low-APR plan'
    },
    {
      name: 'Klarna',
      tagline: 'Global retail BNPL',
      pros: [
        'Pay-in-4 interest-free option',
        'Strong app experience',
        'Wide retail coverage'
      ],
      cons: [
        'No semi-private flights',
        'Doesn\'t redeem airline gift cards or credits itself',
        'Soft credit check (financing plans involve a credit check)',
        'Up to 35.99% APR on financing; late fees up to $7'
      ],
      bestFor: 'Retail shopping; not travel-optimized'
    },
    {
      name: 'Uplift',
      tagline: 'Now Flex Pay by Upgrade, offered by partner airlines',
      pros: [
        'Offered at checkout by airlines such as Southwest, United and Alaska',
        'Travel-focused',
        'No late fees'
      ],
      cons: [
        'No semi-private flights',
        'Combining with airline gift cards or credits depends on the airline',
        'Soft credit check',
        '0%–36% APR, depending on credit',
        'Limited to partner airlines'
      ],
      bestFor: 'Booking directly with a partner airline that offers Flex Pay'
    }
  ];

  const deferaAdvantages = [
    {
      icon: Plane,
      title: 'Semi-Private Flight Access',
      description: 'Defera is the world\'s first BNPL service offering semi-private flights (JSX). Skip the lines, fly in comfort, and pay over time.'
    },
    {
      icon: Gift,
      title: 'Gift Card & Credit Acceptance',
      description: 'The only BNPL that accepts airline gift cards and travel credits. Don\'t let your credits expire—use them with Defera.'
    },
    {
      icon: Shield,
      title: 'No Credit Check',
      description: 'Defera isn\'t a lender and doesn\'t run a credit check, hard or soft. There\'s no credit score requirement.'
    },
    {
      icon: CreditCard,
      title: 'No Interest, Ever',
      description: 'No interest and no hidden fees. The trip total at checkout already includes every fee, and nothing is added later.'
    },
    {
      icon: Clock,
      title: 'Flexible Schedules',
      description: 'Choose weekly or biweekly installments that align with your paycheck. Not locked into rigid 4-payment windows.'
    },
    {
      icon: Zap,
      title: 'Price Protection',
      description: 'Once your booking is approved, your agreed-upon trip price is secured. If fares rise while you\'re paying, your price doesn\'t, as long as you stay in good standing on your plan.'
    }
  ];

  // A table cell: a check for yes, a cross for no, or a short note.
  const renderCell = (value) => {
    if (value === true) return <CheckCircle2 className="w-5 h-5 text-white mx-auto" />;
    if (value === false) return <XCircle className="w-5 h-5 text-red-400/60 mx-auto" />;
    return <span className="text-white/90 text-xs">{value}</span>;
  };

  const faqs = [
    {
      question: 'How is Defera different from Affirm for flights?',
      answer: 'Affirm is a lender: it checks eligibility with a soft credit check and charges 0% to 36% APR depending on the plan, and it doesn\'t offer semi-private flights or redeem airline gift cards. Defera is built specifically for travel with no credit check, no interest, weekly or bi-weekly payments, and unique features like JSX semi-private access and airline credit acceptance.'
    },
    {
      question: 'Can I use Klarna for semi-private flights?',
      answer: 'No. Klarna is a retail-focused BNPL and does not offer semi-private flight options. Defera is the world\'s first BNPL to provide access to semi-private carriers like JSX.'
    },
    {
      question: 'Does Uplift accept airline gift cards?',
      answer: 'It depends on the airline. Uplift, now called Flex Pay by Upgrade, is a lender that partner airlines offer at checkout, so whether it can be combined with an airline gift card or travel credit is set by each airline. Defera is the only BNPL that accepts both.'
    },
    {
      question: 'Which BNPL has no credit check for flights?',
      answer: 'Defera doesn\'t run a credit check at all, hard or soft. Affirm, Klarna and Uplift (Flex Pay by Upgrade) check eligibility with a soft credit check, which doesn\'t affect your credit score, and Klarna\'s longer financing plans involve a credit check. A few smaller layaway services also skip credit checks.'
    },
    {
      question: 'Is Defera available in the US?',
      answer: 'Yes. Defera is available to U.S. travelers and searches major airlines worldwide, including semi-private options like JSX.'
    },
    {
      question: 'What makes Defera the best BNPL for travel?',
      answer: 'Defera combines features no other BNPL offers: semi-private flights, gift card/credit acceptance, no credit checks, no interest, flexible schedules, and price protection. It\'s built from the ground up for travelers.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/defera-vs-affirm-klarna-uplift"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: metaTitle,
            description: metaDescription,
            author: { '@type': 'Organization', name: 'Defera' },
            publisher: {
              '@type': 'Organization',
              name: 'Defera',
              logo: { '@type': 'ImageObject', url: 'https://defera.app/defera-transparent.png' }
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  2026 Comparison
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Defera vs Affirm vs Klarna vs Uplift
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Not all BNPL services are created equal—especially for travel. See how Defera compares to <strong className="text-white">Affirm</strong>, <strong className="text-white">Klarna</strong> and <strong className="text-white">Uplift</strong> (now called Flex Pay by Upgrade) on the features that matter most to travelers. Provider terms are as of November 2026.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Quick Winner */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1d1d1f] border border-white/[0.08] rounded-[1.25rem] p-6 md:p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-6 h-6 text-white" />
                <h2 className="text-xl md:text-2xl font-bold text-white">The bottom line</h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-4">
                <strong className="text-white">Defera is the only BNPL that offers:</strong>
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Semi-private flights (world's first)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">Airline gift card and travel credit acceptance (world's first)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">No credit check + no interest + flexible schedules</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Feature-by-feature comparison</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-semibold">Feature</th>
                      <th className="text-center py-3 px-4 text-white font-semibold">Defera</th>
                      <th className="text-center py-3 px-4 text-white/70 font-semibold">Affirm</th>
                      <th className="text-center py-3 px-4 text-white/70 font-semibold">Klarna</th>
                      <th className="text-center py-3 px-4 text-white/70 font-semibold">Uplift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((row, idx) => (
                      <tr key={idx} className="border-b border-white/[0.08]">
                        <td className="py-3 px-4 text-white/90 font-medium">{row.feature}</td>
                        {['defera', 'affirm', 'klarna', 'uplift'].map((key) => (
                          <td key={key} className="py-3 px-4 text-center">{renderCell(row[key])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Defera Advantages */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Why Defera wins for travelers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {deferaAdvantages.map((advantage, index) => {
                  const Icon = advantage.icon;
                  return (
                    <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <Icon className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div>
                          <h3 className="text-white font-semibold mb-1">{advantage.title}</h3>
                          <p className="text-white/80 text-sm">{advantage.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Provider Details */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Provider breakdown</h2>
              <div className="space-y-6">
                {providerDetails.map((provider, index) => (
                  <div
                    key={index}
                    className={`bg-[#1d1d1f] border rounded-xl p-6 ${provider.name === 'Defera' ? 'border-white/[0.12] bg-white/[0.04]' : 'border-white/[0.08]'}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className={`text-xl font-bold ${provider.name === 'Defera' ? 'text-white' : 'text-white'}`}>
                        {provider.name}
                      </h3>
                      {provider.name === 'Defera' && <Star className="w-5 h-5 text-white/90 fill-white/80" />}
                      <span className="text-white/50 text-sm">— {provider.tagline}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <h4 className="text-white text-sm font-medium mb-2">Pros</h4>
                        <ul className="space-y-1">
                          {provider.pros.map((pro, i) => (
                            <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-red-400 text-sm font-normal mb-2">Cons</h4>
                        <ul className="space-y-1">
                          {provider.cons.map((con, i) => (
                            <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                              <XCircle className="w-4 h-4 text-red-400/60 flex-shrink-0 mt-0.5" />
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <p className="text-white/80 text-sm">
                      <strong className="text-white/90">Best for:</strong> {provider.bestFor}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 px-6 bg-zinc-950/50 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to try the travel-first BNPL?</h2>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                Experience what Affirm, Klarna, and Uplift can't offer: semi-private flights, gift card acceptance, and truly flexible payment plans.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/search')} className="bg-white hover:bg-white/90 text-black px-6 py-3 rounded-full font-semibold text-sm">
                  Search Flights
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/how-it-works')}
                  className="border border-white/15 text-white hover:bg-white/10 px-6 py-3 rounded-full font-semibold text-sm"
                >
                  How Defera Works
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/20 transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/book-flights-without-klarna-or-affirm" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  How to book flights without Klarna or Affirm
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/does-buy-now-pay-later-affect-credit-score" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Does buy now, pay later affect your credit score?
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-layaway-vs-bnpl" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight layaway vs BNPL: what’s the difference?
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/pay-flights-with-gift-cards-credits" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Pay for flights with airline gift cards & credits
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
